const MOBILE_WIDTH = 989

class Megamenu extends HTMLElement {
    constructor() {
        super();

        this.activeElements = {
            "panes": {},
            "links": {}
        };

        this.backLinks = this.getElementsByClassName("megamenu__back-link");

        this.hamburger = this.querySelector("header-megamenu-hamburger");

        if (!this.backLinks) {
            console.error("Required DOM elements not found.");
            return;
        }

        this._setupHandlers()
        this.megamenuOpen = false
    }

    // Public methods
    close() {
        this.megamenuOpen = false
        document.body.classList.remove('megamenu-open')

        for (const key of Object.keys(this.activeElements.panes)) {
            this._removeHistoryStateByKey(key)
        }

        document.dispatchEvent(new CustomEvent("megamenuStateChanged", { detail: { isOpen: false } }));
    }

    open() {
        this.megamenuOpen = true
        const basePane = this.querySelector(`.megamenu__linklist[data-submenu-id="base"]`)
        document.body.classList.add('megamenu-open')

        basePane.classList.add('visible')
        this.activeElements.panes[0] = basePane

        this._resetLinksClicked()

        document.dispatchEvent(new CustomEvent("megamenuStateChanged", { detail: { isOpen: true } }));

        this.focus()
    }

    // Event Handlers
    _handleNavigation(event) {
        event.preventDefault()

        if (event.target.classList.contains("active") &&
            event.target.classList.contains("megamenu__top-link")
        ) {
            this.close()
            return
        }

        const level = event.target.dataset.level
        const animate = !!!this.activeElements.panes[level]

        if (!this.megamenuOpen) this.open()

        this._addActiveLink(event, level);
        this._cleanExtraPanes(level)
        this._addActivePane(animate, event, level);
        this._resetFinalPaneClass();
        this._resetLinksClicked();
        this._addAttribetesToClickedLink(event.target)
    }


    _addAttribetesToClickedLink(target) {
        target.setAttribute("aria-expanded", true)
    }

    _handleBackLinkClicks() {
        for (const backLink of this.backLinks) {
            backLink.addEventListener('click', (e) => {
                const level = e.target.dataset.level

                this._removeHistoryStateByKey(level)

                if (this.activeElements.links[level - 1]) {
                    this.activeElements.links[level - 1].classList.remove("active")
                    delete this.activeElements.links[level - 1]
                }

                this.focus()

                if (window.innerWidth < MOBILE_WIDTH && Object.keys(this.activeElements.panes).length ||
                    Object.keys(this.activeElements.links).length
                ) return

                for (const key of Object.keys(this.activeElements.panes)) {
                    this._removeHistoryStateByKey(key)
                }
            })
        }
    }

    _closeMegamenuOnOutsideClick() {
        document.addEventListener("click", (event) => {
            event.stopPropagation()
            const target = event.target

            const mobileMenuDrawerParent = target.closest(".mobile-menu-drawer-parent");
            const megamenu = target.closest("header-megamenu");
            const hamburger = target.closest("header-megamenu-hamburger");

            if ( mobileMenuDrawerParent || megamenu || hamburger || event.target.classList.contains("sustain-mobile-text-field")) return

            this.close();
        });
    }

    _closeMegamenuOnEscapeKey() {
        document.addEventListener("keydown", (event) => {
            if (event.code === "Escape") {
                this.close();
            }
        });
    }

    _setupNavLinkEventListeners() {
        this.addEventListener("click", (e) => {
            const target = e.target.closest(".has-submenu");

            if (target) {
                this._handleNavigation(e);
            }
        });
        this.addEventListener("keydown", (e) => {
            const target = e.target.closest(".has-submenu");

            if ((e.code === "Enter" || e.code === "Space") && target) {
                e.preventDefault()
                this._handleNavigation(e);
            }
        })
    }

    //Helper Methods
    _setupHandlers() {
        this._setupNavLinkEventListeners()
        this._closeMegamenuOnOutsideClick()
        this._closeMegamenuOnEscapeKey()
        this._handleBackLinkClicks()
        this._megamenuHamburgerStateChange()
        this._closeOnDesktopIfOnlyBasePanePresent()
    }

    _closeOnDesktopIfOnlyBasePanePresent() {
        window.addEventListener("resize", () => {
            if (window.innerWidth > MOBILE_WIDTH &&
                Object.keys(this.activeElements.panes).length == 1 &&
                Object.keys(this.activeElements.panes).includes("0")
            ) this.close()
        })
    }

    _megamenuHamburgerStateChange() {
        document.addEventListener("hamburgerStateChanged", (event) => {
            const isOpen = event.detail.isOpen;

            if (isOpen) {
                this.open();
            } else {
                this.close();
            }
        });
    }

    _removeHistoryStateByKey(key) {
        if (this.activeElements.panes[key]) {
            this.activeElements.panes[key].classList.remove("visible")
            delete this.activeElements.panes[key]
        }

        if (this.activeElements.links[key]) {
            this.activeElements.links[key].classList.remove("active")
            this.activeElements.links[key].removeAttribute("aria-expanded")
            delete this.activeElements.links[key]
        }

        this._resetFinalPaneClass()
    }

    _resetFinalPaneClass() {
        Object.entries(this.activeElements.panes).forEach(([key, pane]) => {
            pane.classList.remove("megamenu__final-pane");
        });

        const finalPane = this.activeElements.panes[Object.keys(this.activeElements.panes).pop()];

        if (finalPane) {
            finalPane.classList.add("megamenu__final-pane");

            requestAnimationFrame(() => {
                finalPane.focus()
                const finalPaneInner = finalPane.querySelector(".megamenu__linklist--inner-container")

                finalPaneInner.focus()
                finalPane.scrollTop = 0
            })

            window.setTimeout(() => {
                finalPane.scrollTop = 0
            }, 50)
        }
    }

    _cleanExtraPanes(level) {
        for (const key of Object.keys(this.activeElements.panes)) {
            if (key < level) continue

            this._removeHistoryStateByKey(key)
        }
    }


    _addActiveLink(event, level) {
        const link = event.target
        this.activeElements.links[level - 1] = link
    }

    _resetLinksClicked() {
        const links = this.querySelectorAll('a.active')

        for (const link of links) {
            link.classList.remove("active")
        }

        for (const linkKey of Object.keys(this.activeElements.links)) {
            this.activeElements.links[linkKey].classList.add("active")
        }
    }

    _addActivePane(animate, event, level) {
        const paneElement = this.querySelector(`.megamenu__linklist[data-submenu-id="${event.target.dataset.submenuId}"]`);
        if (!paneElement) return;

        if (animate) {
            paneElement.classList.add('animate');

            paneElement.addEventListener("transitionend", () => {
                paneElement.classList.remove("animate");
            }, { once: true });
        }

        paneElement.classList.add('visible');

        this.activeElements.panes[level] = paneElement;
    }
}

customElements.define("header-megamenu", Megamenu);

class MegamenuHamburger extends HTMLElement {
    constructor() {
        super();
        this.isOpen = false;
        this.buttonElement = this.querySelector("button")

        this.buttonElement.addEventListener("click", (event) => this.toggleMegamenu(event));

        document.addEventListener("megamenuStateChanged", (event) => {
            const isOpen = event.detail.isOpen;
            this._setOpenState(isOpen);
        });
    }

    toggleMegamenu(event) {
        event.stopPropagation()
        this.isOpen = !this.isOpen;
        this.classList.toggle("open", this.isOpen)

        document.dispatchEvent(new CustomEvent("hamburgerStateChanged", { detail: { isOpen: this.isOpen } }));
    }

    _setOpenState(open) {
        this.isOpen = open
        this.classList.toggle("open", this.isOpen)
        this.buttonElement.setAttribute("aria-expanded", open ? "true" : "false")
    }
}

customElements.define('header-megamenu-hamburger', MegamenuHamburger);



